import FilterRangeSliderField from "./fields/FilterRangeSliderField";
import FilterRangeDropdownField from "./fields/FilterRangeDropdownField";
import FilterMultiSelectField from "./fields/FilterMultiSelectField";
import FilterLocalSearchField from "./fields/FilterLocalSearchField";
import FilterSortField from "./fields/FilterSortField";
import FilterSingleSelectField from "./fields/FilterSingleSelectField";
import FilterDateRangeField from "./fields/FilterDateRangeField";
import { FILTER_ROW_DATE_RANGE_ITEM_SX } from "./rewampFilterLayout";

export const FILTER_FIELD_KINDS = {
  rangeSlider: {
    render({ descriptor, controlId, value, onChange, ctx }) {
      const bounds = ctx[descriptor.boundsKey];
      if (!bounds) return null;
      return (
        <FilterRangeSliderField
          controlId={controlId}
          labelKey={descriptor.labelKey}
          label={descriptor.label}
          bounds={bounds}
          value={value}
          onChange={onChange}
          formatValue={ctx.formatValue ?? String}
        />
      );
    },
  },
  rangeDropdown: {
    render({ descriptor, controlId, value, onChange, ctx, dropdownVariant }) {
      const bounds = ctx[descriptor.boundsKey];
      if (!bounds) return null;
      return (
        <FilterRangeDropdownField
          controlId={controlId}
          labelKey={descriptor.labelKey}
          label={descriptor.label}
          panelTitleKey={descriptor.panelTitleKey}
          placeholderKey={descriptor.placeholderKey}
          bounds={bounds}
          value={value}
          onChange={onChange}
          formatValue={ctx.formatValue ?? String}
          dropdownVariant={dropdownVariant}
        />
      );
    },
  },
  multiSelect: {
    render({ descriptor, controlId, value, onChange, ctx, dropdownVariant }) {
      return (
        <FilterMultiSelectField
          controlId={controlId}
          labelKey={descriptor.labelKey}
          label={descriptor.label}
          options={ctx[descriptor.optionsKey] ?? []}
          loading={Boolean(descriptor.loadingKey && ctx[descriptor.loadingKey])}
          value={value}
          onChange={onChange}
          getOptionLabel={descriptor.getOptionLabel}
          isOptionEqualToValue={descriptor.isOptionEqualToValue}
          placeholderKey={descriptor.placeholderKey}
          maxSelections={descriptor.maxSelections}
          dropdownVariant={dropdownVariant}
        />
      );
    },
  },
  search: {
    render({ descriptor, controlId, value, onChange, ctx, dropdownVariant }) {
      return (
        <FilterLocalSearchField
          dropdownVariant={dropdownVariant}
          controlId={controlId}
          labelKey={descriptor.labelKey}
          label={descriptor.label}
          ariaLabelKey={descriptor.ariaLabelKey}
          ariaLabel={descriptor.ariaLabel}
          placeholderKey={descriptor.placeholderKey}
          placeholder={descriptor.placeholder}
          iconColor={descriptor.iconColor ?? ctx.searchIconColor}
          value={value}
          onChange={onChange}
        />
      );
    },
  },
  singleSelect: {
    render({ descriptor, controlId, value, onChange, ctx, dropdownVariant, error, describedBy }) {
      return (
        <FilterSingleSelectField
          controlId={controlId}
          clearable={Boolean(descriptor.clearable)}
          error={error}
          describedBy={describedBy}
          dropdownVariant={dropdownVariant}
          labelKey={descriptor.labelKey}
          label={descriptor.label}
          options={ctx[descriptor.optionsKey] ?? []}
          value={value}
          onChange={onChange}
          compact={descriptor.compact ?? false}
        />
      );
    },
  },
  dateRange: {
    render({ descriptor, controlId, value, onChange, error, describedBy }) {
      return (
        <FilterDateRangeField
          controlId={controlId}
          value={value}
          onChange={onChange}
          error={error}
          describedBy={describedBy}
          clearable={descriptor.clearable !== false}
        />
      );
    },
    itemSx: FILTER_ROW_DATE_RANGE_ITEM_SX,
    isEmpty: (value) => !value?.from && !value?.to,
    isIncomplete: (value) => Boolean(value?.from) !== Boolean(value?.to),
    incompleteKey: "FILTER_ROW.DATE_BOTH_REQUIRED",
  },
  sort: {
    render({ descriptor, controlId, value, onChange, ctx, dropdownVariant }) {
      return (
        <FilterSortField
          controlId={controlId}
          dropdownVariant={dropdownVariant}
          labelKey={descriptor.labelKey ?? "FILTER_ROW.SORT_BY"}
          label={descriptor.label}
          options={ctx[descriptor.optionsKey] ?? []}
          value={value}
          onChange={onChange}
          compact={descriptor.compact ?? true}
        />
      );
    },
  },
};

/**
 * Dependency Injection API for registering domain-specific fields
 * into the generic filter framework.
 */
export function registerFilterFieldKind(kind, renderFn) {
  FILTER_FIELD_KINDS[kind] = { render: renderFn };
}

export const getFilterItemSx = (descriptor) => ({ ...FILTER_FIELD_KINDS[descriptor.kind]?.itemSx, ...descriptor.itemSx });

export function renderFilterField(descriptor, props) {
  return FILTER_FIELD_KINDS[descriptor.kind]?.render({ descriptor, ...props }) ?? null;
}

const isDefaultEmpty = (value) => value == null || value === "" || (Array.isArray(value) && value.length === 0);

export const isFilterValueEmpty = (descriptor, value) => (FILTER_FIELD_KINDS[descriptor.kind]?.isEmpty ?? isDefaultEmpty)(value);

export const hasActiveFilters = (fields, values = {}) =>
  fields.some((descriptor) => !isFilterValueEmpty(descriptor, values[descriptor.id]));

// { fieldId: messageKey } for fields that hold the filter back: a `dependsOn` child left empty
// after its parent is picked, or a kind whose value is half-set (e.g. one date of a range).
export const getPendingFilterFields = (fields, values = {}) => {
  const byId = Object.fromEntries(fields.map((descriptor) => [descriptor.id, descriptor]));
  return fields.reduce((pending, descriptor) => {
    const value = values[descriptor.id];
    const kind = FILTER_FIELD_KINDS[descriptor.kind];
    const parent = descriptor.dependsOn ? byId[descriptor.dependsOn] : null;
    if (parent && !isFilterValueEmpty(parent, values[parent.id]) && isFilterValueEmpty(descriptor, value)) {
      pending[descriptor.id] = "FILTER_ROW.REQUIRED_TO_APPLY";
    } else if (kind?.isIncomplete?.(value)) {
      pending[descriptor.id] = kind.incompleteKey;
    }
    return pending;
  }, {});
};
